import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import {
  type BrandPrintLanguage,
  withBrandPrintLanguage,
} from "@/shared/lib/brand-document-print-language";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type ExpenseRecordListItemDTO = {
  id: string;
  number: string;
  expenseDate: string;
  categoryId: string;
  categoryName: string;
  payeeName: string;
  amount: number;
  status: string;
  createdBy: string;
  createdByName: string | null;
  performanceEventId: string | null;
  createdAt: string;
};

export type ExpenseAttachmentDTO = {
  id: string;
  storageKey: string;
  fileName: string;
  contentType: string | null;
  uploadedBy: string;
  uploadedByName: string | null;
  createdAt: string;
};

export type ExpenseRecordDetailDTO = {
  id: string;
  number: string;
  expenseDate: string;
  categoryId: string;
  categoryName: string;
  categoryCode: string;
  payeeName: string;
  amount: number;
  description: string | null;
  status: string;
  paymentMethod: string | null;
  paidAt: string | null;
  paidBy: string | null;
  paidByName: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  approvedByName: string | null;
  rejectedAt: string | null;
  rejectedBy: string | null;
  rejectedByName: string | null;
  rejectionReason: string | null;
  voidedAt: string | null;
  voidedBy: string | null;
  voidedByName: string | null;
  voidReason: string | null;
  performanceEventId: string | null;
  performanceEventTitle: string | null;
  notes: string | null;
  createdBy: string;
  createdByName: string | null;
  submittedAt: string | null;
  closedAt: string | null;
  closedBy: string | null;
  closedByName: string | null;
  createdAt: string;
  updatedAt: string;
  attachments: ExpenseAttachmentDTO[];
};

export type ExpensesSummaryDTO = {
  totalCount: number;
  totalAmount: number;
  draftCount: number;
  submittedCount: number;
  approvedCount: number;
  rejectedCount: number;
  paidCount: number;
  closedCount: number;
  cancelledCount: number;
  voidedCount: number;
  draftAmount: number;
  submittedAmount: number;
  approvedAmount: number;
  paidAmount: number;
};

export type ExpenseCategoryDTO = {
  id: string;
  code: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type ExpensesSummaryQuery = {
  filters?: Array<{ field: string; op: string; value: unknown }>;
};

export type ExpensesOverviewChartsDTO = {
  byStatus: Array<{ status: string; count: number; amount: number }>;
  byMonth: Array<{ month: string; amount: number; count: number }>;
  byCategory: Array<{
    categoryId: string;
    categoryName: string;
    amount: number;
    count: number;
  }>;
};

function buildListUrl(path: string, query: OffsetPageQueryDTO): string {
  const url = new URL(`${config.apiUrl}${path}`);
  url.searchParams.set("limit", String(query.limit ?? 20));
  url.searchParams.set("offset", String(query.offset ?? 0));
  if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
  if (query.filters)
    url.searchParams.set("filters", JSON.stringify(query.filters));
  return url.toString();
}

export const expensesApi = {
  records: {
    list(query: OffsetPageQueryDTO) {
      return fetcher.get<{
        data: ExpenseRecordListItemDTO[];
        meta: { total: number; offset: number; limit: number };
      }>(buildListUrl("/expenses/records", query));
    },
    getSummary(query: ExpensesSummaryQuery) {
      const url = new URL(`${config.apiUrl}/expenses/records/summary`);
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<ExpensesSummaryDTO>(url.toString());
    },
    getOverviewCharts() {
      return fetcher.get<ExpensesOverviewChartsDTO>(
        `${config.apiUrl}/expenses/records/overview-charts`,
      );
    },
    getById(id: string) {
      return fetcher.get<ExpenseRecordDetailDTO>(
        `${config.apiUrl}/expenses/records/${id}`,
      );
    },
    printUrl(id: string, lang: BrandPrintLanguage = "lo"): string {
      return withBrandPrintLanguage(
        `${config.apiUrl}/expenses/records/${id}/print`,
        lang,
      );
    },
    create(data: {
      expenseDate: string;
      categoryId: string;
      payeeName: string;
      amount: number;
      description?: string;
      performanceEventId?: string;
      notes?: string;
      submit?: boolean;
    }) {
      return fetcher.post<{ id: string }>(
        `${config.apiUrl}/expenses/records`,
        data,
      );
    },
    update(
      id: string,
      data: Partial<{
        expenseDate: string;
        categoryId: string;
        payeeName: string;
        amount: number;
        description?: string;
        performanceEventId?: string;
        notes?: string;
      }>,
    ) {
      return fetcher.patch<{ success: boolean }>(
        `${config.apiUrl}/expenses/records/${id}`,
        data,
      );
    },
    submit(id: string) {
      return fetcher.post<{ success: boolean }>(
        `${config.apiUrl}/expenses/records/${id}/submit`,
        {},
      );
    },
    approve(id: string) {
      return fetcher.post<{ success: boolean }>(
        `${config.apiUrl}/expenses/records/${id}/approve`,
        {},
      );
    },
    reject(id: string, data: { rejectionReason?: string }) {
      return fetcher.post<{ success: boolean }>(
        `${config.apiUrl}/expenses/records/${id}/reject`,
        data,
      );
    },
    markPaid(
      id: string,
      data: {
        paymentMethod: "cash" | "bank_transfer" | "other";
        paidAt?: string;
        notes?: string;
      },
    ) {
      return fetcher.post<{ success: boolean }>(
        `${config.apiUrl}/expenses/records/${id}/mark-paid`,
        data,
      );
    },
    close(id: string) {
      return fetcher.post<{ success: boolean }>(
        `${config.apiUrl}/expenses/records/${id}/close`,
        {},
      );
    },
    cancel(id: string) {
      return fetcher.post<{ success: boolean }>(
        `${config.apiUrl}/expenses/records/${id}/cancel`,
        {},
      );
    },
    void(id: string, data: { voidReason: string }) {
      return fetcher.post<{ success: boolean }>(
        `${config.apiUrl}/expenses/records/${id}/void`,
        data,
      );
    },
    addAttachment(
      id: string,
      data: { storageKey: string; fileName: string; contentType?: string },
    ) {
      return fetcher.post<{ id: string }>(
        `${config.apiUrl}/expenses/records/${id}/attachments`,
        data,
      );
    },
    deleteAttachment(id: string, attachmentId: string) {
      return fetcher.delete<{ success: boolean }>(
        `${config.apiUrl}/expenses/records/${id}/attachments/${attachmentId}`,
      );
    },
  },
  categories: {
    list() {
      return fetcher.get<{ data: ExpenseCategoryDTO[] }>(
        `${config.apiUrl}/expenses/categories`,
      );
    },
    create(data: {
      code: string;
      name: string;
      parentId?: string;
      sortOrder?: number;
      isActive?: boolean;
    }) {
      return fetcher.post<{ id: string }>(
        `${config.apiUrl}/expenses/categories`,
        data,
      );
    },
    update(
      id: string,
      data: Partial<{
        code: string;
        name: string;
        parentId: string | null;
        sortOrder: number;
        isActive: boolean;
      }>,
    ) {
      return fetcher.patch<{ success: boolean }>(
        `${config.apiUrl}/expenses/categories/${id}`,
        data,
      );
    },
  },
};
